import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";
import TeamBadgeList from "../components/TeamBadgeList";
import { ROLE_CLASS, ROLE_LABEL } from "../utils/roleLabels";

const MODULE_OPTIONS = [
  { value: "inventory",  label: "Inventory" },
  { value: "sekretaris", label: "Sekretaris" },
  { value: "bendahara",  label: "Bendahara" },
];

const MODULE_LABEL = MODULE_OPTIONS.reduce((a, o) => ({ ...a, [o.value]: o.label }), {});

/* ── icons (inline, 24-grid, same stroke style as the Lisokom design) ── */

const Svg = ({ size = 16, sw = 1.8, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const LockIcon = () => (
  <Svg size={18}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></Svg>
);
const WarnIcon = () => (
  <Svg size={18}><path d="M12 4l9 16H3z" /><path d="M12 10v4" /><path d="M12 17v.5" /></Svg>
);
const ChevronRightIcon = () => (
  <Svg size={14} sw={2}><path d="M9 6l6 6-6 6" /></Svg>
);
const InfoIcon = () => (
  <Svg size={14} sw={2}><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8v.5" /></Svg>
);
const PlusIcon = () => (
  <Svg size={16} sw={2.4}><path d="M12 5v14" /><path d="M5 12h14" /></Svg>
);
const UsersIcon = () => (
  <Svg size={15}>
    <circle cx="9" cy="9" r="3.5" /><path d="M3 19c0-3 3-5 6-5s6 2 6 5" />
    <path d="M16 6a3 3 0 0 1 0 6" /><path d="M18 14c2 .6 3 2.3 3 5" />
  </Svg>
);
const PencilIcon = () => (
  <Svg size={15}><path d="M4 20h4L19 9l-4-4L4 16z" /></Svg>
);
const TrashIcon = () => (
  <Svg size={15}><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="M6 7l1 13h10l1-13" /></Svg>
);

const scrollToId = (id) =>
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function Roles() {
  const [teams, setTeams]             = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [users, setUsers]             = useState([]);
  const [locations, setLocations]     = useState([]);
  const [me, setMe]                   = useState(null);
  const [scoping, setScoping]         = useState(null);
  const [savingScoping, setSaving]    = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [notice, setNotice]   = useState("");

  const [newName, setNewName]           = useState("");
  const [newModule, setNewModule]       = useState("inventory");
  const [newApprover, setNewApprover]   = useState("");
  const [newDescription, setNewDesc]    = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});

  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [addUserId, setAddUserId]           = useState("");

  const token = localStorage.getItem("accessToken");
  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const readError = async (res, fallback) => {
    const d = await res.json().catch(() => null);
    if (!d) return fallback;
    if (typeof d === "string") return d;
    if (d.detail) return d.detail;
    const first = Object.values(d)[0];
    if (Array.isArray(first)) return String(first[0]);
    return typeof first === "string" ? first : fallback;
  };

  const loadAll = useCallback(() => {
    setLoading(true);
    // /api/teams/scoping/ is superadmin-only; a plain admin opening this page
    // gets a 403 there, which must not take the rest of the page down with it.
    const optional = (url) =>
      fetch(url, { headers: authHeaders }).then((r) => (r.ok ? r.json() : null)).catch(() => null);

    Promise.all([
      fetch(`${API_BASE_URL}/api/teams/`,            { headers: authHeaders }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/team-memberships/`, { headers: authHeaders }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/users/`,            { headers: authHeaders }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/locations/`,        { headers: authHeaders }).then((r) => r.json()),
      fetch(`${API_BASE_URL}/api/auth/me/`,          { headers: authHeaders }).then((r) => r.json()),
      optional(`${API_BASE_URL}/api/teams/scoping/`),
    ])
      .then(([t, m, u, l, meData, sc]) => {
        setTeams(Array.isArray(t) ? t : []);
        setMemberships(Array.isArray(m) ? m : []);
        setUsers(Array.isArray(u) ? u : []);
        setLocations(Array.isArray(l) ? l : []);
        setMe(meData && meData.id ? meData : null);
        setScoping(sc);
      })
      .catch(() => setError("Gagal memuat data kelompok."))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const flash = (msg) => {
    setNotice(msg);
    setError("");
    window.setTimeout(() => setNotice(""), 3500);
  };

  const membersOf = (teamId) => memberships.filter((m) => m.team_id === teamId);

  const isSuperadmin = me?.role === "superadmin";

  /* ── saklar pembatasan ─────────────────────────────────────────── */

  const handleToggleScoping = (next) => {
    // Confirmed both ways: switching on can blind people who aren't in a
    // kelompok yet, switching off silently reopens everyone's data to everyone.
    const warn = next
      ? "Nyalakan pembatasan per-kelompok?\n\n" +
        "Stok semua gudang tetap terlihat oleh semua kelompok inventory — " +
        "mereka tetap bisa request seperti biasa.\n\n" +
        "Yang berubah: untuk role \"user\", pengajuan cuma kelihatan oleh " +
        "kelompok pengaju, kelompok yang meng-ACC, dan pemilik gudangnya. " +
        "Tim Barat (role user) tidak lagi melihat pengajuan Tim Timur.\n\n" +
        "Admin dan superadmin tetap melihat dan meng-ACC semuanya, apapun " +
        "kelompoknya. Bisa dimatikan lagi kapan saja."
      : "Matikan pembatasan per-kelompok?\n\n" +
        "Role \"user\" akan kembali melihat pengajuan semua kelompok, bukan cuma " +
        "kelompoknya sendiri.";
    if (!window.confirm(warn)) return;

    setSaving(true);
    setError("");
    fetch(`${API_BASE_URL}/api/teams/scoping/`, {
      method: "PUT",
      headers: authHeaders,
      body: JSON.stringify({ enabled: next }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal mengubah pembatasan."));
        return res.json();
      })
      .then(() => {
        flash(next ? "Pembatasan per-kelompok dinyalakan." : "Pembatasan per-kelompok dimatikan.");
        loadAll();
      })
      .catch((err) => setError(err.message))
      .finally(() => setSaving(false));
  };

  /* ── kelompok CRUD ─────────────────────────────────────────────── */

  const handleCreate = (e) => {
    e.preventDefault();
    setError("");
    if (!newName.trim()) { setError("Nama kelompok wajib diisi."); return; }

    fetch(`${API_BASE_URL}/api/teams/`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({
        name: newName.trim(),
        module: newModule,
        description: newDescription.trim(),
        ...(newApprover ? { default_approver_team_id: Number(newApprover) } : {}),
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal membuat kelompok."));
        return res.json();
      })
      .then(() => {
        setNewName(""); setNewModule("inventory"); setNewApprover(""); setNewDesc("");
        flash("Kelompok dibuat.");
        loadAll();
      })
      .catch((err) => setError(err.message));
  };

  const startEdit = (team) => {
    setEditingId(team.id);
    setEditDraft({
      name: team.name,
      module: team.module,
      default_approver_team_id: team.default_approver_team_id ?? "",
      description: team.description || "",
      is_active: team.is_active,
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft({}); };

  const saveEdit = () => {
    setError("");
    fetch(`${API_BASE_URL}/api/teams/${editingId}/`, {
      method: "PATCH",
      headers: authHeaders,
      body: JSON.stringify({
        name: (editDraft.name || "").trim(),
        module: editDraft.module,
        description: (editDraft.description || "").trim(),
        is_active: !!editDraft.is_active,
        default_approver_team_id: editDraft.default_approver_team_id
          ? Number(editDraft.default_approver_team_id)
          : null,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal menyimpan kelompok."));
        return res.json();
      })
      .then(() => { cancelEdit(); flash("Kelompok disimpan."); loadAll(); })
      .catch((err) => setError(err.message));
  };

  const handleDeleteTeam = (team) => {
    if (!window.confirm(`Hapus kelompok "${team.name}"?`)) return;
    setError("");
    fetch(`${API_BASE_URL}/api/teams/${team.id}/`, { method: "DELETE", headers: authHeaders })
      .then(async (res) => {
        if (res.status !== 204 && !res.ok) throw new Error(await readError(res, "Gagal menghapus kelompok."));
        flash("Kelompok dihapus.");
        loadAll();
      })
      .catch((err) => setError(err.message));
  };

  /* ── anggota ───────────────────────────────────────────────────── */

  const handleAddMember = (teamId) => {
    setError("");
    if (!addUserId) { setError("Pilih user dulu."); return; }

    fetch(`${API_BASE_URL}/api/team-memberships/`, {
      method: "POST",
      headers: authHeaders,
      body: JSON.stringify({ user_id: Number(addUserId), team_id: teamId }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal menambah anggota."));
        return res.json();
      })
      .then(() => { setAddUserId(""); flash("Anggota ditambahkan."); loadAll(); })
      .catch((err) => setError(err.message));
  };

  const handleRemoveMember = (m) => {
    if (!window.confirm(`Keluarkan ${m.username} dari ${m.team_name}?`)) return;
    setError("");
    fetch(`${API_BASE_URL}/api/team-memberships/${m.id}/`, { method: "DELETE", headers: authHeaders })
      .then(async (res) => {
        if (res.status !== 204 && !res.ok) throw new Error(await readError(res, "Gagal mengeluarkan anggota."));
        setMemberships((prev) => prev.filter((x) => x.id !== m.id));
        setTeams((prev) =>
          prev.map((t) => (t.id === m.team_id ? { ...t, member_count: Math.max(0, t.member_count - 1) } : t))
        );
        flash("Anggota dikeluarkan.");
      })
      .catch((err) => setError(err.message));
  };

  /* ── kepemilikan gudang ────────────────────────────────────────── */

  // `field` is "owner_team_id" or "approver_team_id" — the two are set the
  // same way and differ only in what they mean, so one handler covers both.
  const patchLocationTeam = (locationId, field, teamId, doneMsg) => {
    setError("");
    fetch(`${API_BASE_URL}/api/locations/${locationId}/`, {
      method: "PATCH",
      headers: authHeaders,
      body: JSON.stringify({ [field]: teamId ? Number(teamId) : null }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(await readError(res, "Gagal mengubah kelompok gudang."));
        return res.json();
      })
      .then((updated) => {
        setLocations((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
        flash(doneMsg);
        loadAll();
      })
      .catch((err) => setError(err.message));
  };

  /* ── options ───────────────────────────────────────────────────── */

  const approverOptions = useMemo(
    () => [
      { value: "", label: "— tidak ada —" },
      ...teams.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [teams]
  );

  const inventoryTeams = useMemo(
    () => teams.filter((t) => t.module === "inventory"),
    [teams]
  );

  const ownerOptions = useMemo(
    () => [
      { value: "", label: "— belum ada pemilik —" },
      ...inventoryTeams.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [inventoryTeams]
  );

  const gudangApproverOptions = useMemo(
    () => [
      { value: "", label: "— sama dengan pemilik —" },
      ...inventoryTeams.map((t) => ({ value: String(t.id), label: t.name })),
    ],
    [inventoryTeams]
  );

  // Superadmin is global by design and the API rejects putting it in a team,
  // so don't offer it here either.
  const assignableUsers = useMemo(
    () => users.filter((u) => u.role !== "superadmin"),
    [users]
  );

  // Member rows only carry {user_id, username, scope} from the API — this
  // looks up the rest (role, other kelompok) from the users list already
  // loaded for the page, with no extra fetch.
  const usersById = useMemo(
    () => new Map(users.map((u) => [u.id, u])),
    [users]
  );

  const usersNotIn = (team) => {
    const taken = new Set(membersOf(team.id).map((m) => m.user_id));
    return [
      { value: "", label: "Pilih user..." },
      ...assignableUsers
        .filter((u) => !taken.has(u.id))
        // UX pre-filter mirroring the backend's same-module rule for role
        // "user" (case 3) — keeps a candidate who's already locked into a
        // different module out of the list instead of letting them pick it
        // and get rejected. The API still enforces this either way.
        .filter((u) => {
          if (u.role !== "user") return true;
          const otherModules = new Set((u.teams || []).map((t) => t.module));
          return otherModules.size === 0 || otherModules.has(team.module);
        })
        .map((u) => {
          const otherNames = (u.teams || []).map((t) => t.name);
          const hint = otherNames.length ? ` — sudah di: ${otherNames.join(", ")}` : "";
          return { value: String(u.id), label: `${u.username} (${u.role})${hint}` };
        }),
    ];
  };

  const usersWithoutTeam = useMemo(() => {
    const withTeam = new Set(memberships.map((m) => m.user_id));
    return assignableUsers.filter((u) => !withTeam.has(u.id));
  }, [assignableUsers, memberships]);

  // The two things that decide whether it is safe to flip the scoping switch.
  // `target` is the section the "Atur" link scrolls to; `hint` keeps the
  // consequence the old inline copy spelled out, as a tooltip on the row.
  const scopingWarnings = useMemo(() => {
    if (!scoping) return [];
    const noTeam = scoping.users_without_team || [];
    const noOwner = scoping.locations_without_owner || [];
    const out = [];
    if (noTeam.length) {
      out.push({
        key: "users",
        title: `${noTeam.length} user tanpa kelompok`,
        who: noTeam.join(", "),
        hint: "Tanpa kelompok mereka tidak bisa request (stok gudang tidak kelihatan). Masukkan ke kelompok inventory dulu.",
        target: "daftar-kelompok",
      });
    }
    if (noOwner.length) {
      out.push({
        key: "locations",
        title: `${noOwner.length} gudang tanpa pemilik`,
        who: noOwner.join(", "),
        hint: "Pengajuannya cuma bisa di-ACC superadmin. Atur pemiliknya di Kepemilikan Gudang.",
        target: "kepemilikan-gudang",
      });
    }
    return out;
  }, [scoping]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1.5">
        <h1 className="page-title">Role &amp; Kelompok</h1>
        <p className="text-[14px]" style={{ color: "var(--text-secondary)" }}>
          Kelompok = siapa mengurus apa. Role = level akses.
        </p>
      </div>

      {error && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--danger)", background: "var(--danger-subtle)", borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-xl px-4 py-3 text-[13px] border animate-fade-in"
          style={{ color: "var(--success)", background: "var(--success-subtle)", borderColor: "color-mix(in srgb, var(--success) 30%, transparent)" }}>
          {notice}
        </div>
      )}
      {!loading && !isSuperadmin && (
        <div className="rounded-xl px-4 py-3 text-[13px] border"
          style={{ color: "var(--text-secondary)", background: "var(--bg-surface-hover)", borderColor: "var(--border)" }}>
          Kamu bisa melihat struktur kelompok, tapi hanya superadmin yang boleh mengubahnya.
        </div>
      )}
      {/* ── saklar pembatasan per-kelompok ── */}
      {!loading && isSuperadmin && scoping && (
        <div className="glass-panel overflow-hidden">
          <div className="flex items-center gap-3.5 px-5 py-[18px]">
            <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg"
              style={{ background: "var(--accent-subtle)", color: "var(--accent)" }}>
              <LockIcon />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
              <h2 className="text-[15px] font-semibold" style={{ color: "var(--text-primary)" }}>
                Pembatasan per-kelompok
              </h2>
              <p className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                {scoping.enabled
                  ? "Aktif — pengajuan hanya terlihat oleh kelompoknya. Stok tetap terlihat semua."
                  : "Nonaktif — semua pengajuan terlihat semua user."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!!scoping.enabled}
              aria-label="Pembatasan per-kelompok"
              title={savingScoping ? "Menyimpan…" : scoping.enabled ? "Matikan pembatasan" : "Nyalakan pembatasan"}
              disabled={savingScoping}
              onClick={() => handleToggleScoping(!scoping.enabled)}
              className="switch"
            />
          </div>

          {/* The two things that decide whether it is safe to flip the switch. */}
          {scopingWarnings.length > 0 && (
            <div style={{ borderTop: "1px solid var(--border-subtle)" }}>
              {scopingWarnings.map((w, i) => (
                <div key={w.key} title={w.hint}
                  className="flex items-center gap-3.5 px-5 py-3 text-[14px]"
                  style={i > 0 ? { borderTop: "1px solid var(--border-subtle)" } : undefined}>
                  <span className="flex w-[38px] shrink-0 justify-center" style={{ color: "var(--amber)" }}>
                    <WarnIcon />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">{w.title}</span>
                    <span style={{ color: "var(--text-secondary)" }}> · {w.who}</span>
                  </span>
                  <button type="button" onClick={() => scrollToId(w.target)}
                    className="inline-flex items-center gap-1.5 text-[13px]" style={{ color: "var(--accent)" }}>
                    Atur <ChevronRightIcon />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!loading && !isSuperadmin && usersWithoutTeam.length > 0 && (
        <div className="rounded-xl px-4 py-3 text-[13px] border"
          style={{ color: "var(--text-secondary)", background: "var(--bg-surface-hover)", borderColor: "var(--border)" }}>
          <span className="font-semibold">Belum punya kelompok:</span>{" "}
          {usersWithoutTeam.map((u) => u.username).join(", ")}.
        </div>
      )}

      {/* ── buat kelompok ── */}
      {isSuperadmin && (
        <div className="glass-panel p-5">
          <h2 className="text-[15px] font-semibold mb-3.5" style={{ color: "var(--text-primary)" }}>
            Buat kelompok
          </h2>
          <form onSubmit={handleCreate} className="grid items-end gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <div>
              <label className="block text-[12px] mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Nama *
              </label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                className="glass-input" placeholder="mis. Logistik" required />
            </div>
            <div>
              <label className="block text-[12px] mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Modul *
              </label>
              <CustomSelect value={newModule} onChange={setNewModule} options={MODULE_OPTIONS} />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-[12px] mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Approver cadangan
                <span className="flex cursor-help" style={{ color: "var(--text-faint)" }}
                  title="Meng-ACC pengajuan tanpa gudang (mis. peminjaman batch). Kalau ada gudangnya, pemilik gudang yang meng-ACC.">
                  <InfoIcon />
                </span>
              </label>
              <CustomSelect value={newApprover} onChange={setNewApprover} options={approverOptions}
                placeholder="— tidak ada —" />
            </div>
            <div>
              <label className="block text-[12px] mb-1.5" style={{ color: "var(--text-secondary)" }}>
                Keterangan
              </label>
              <input type="text" value={newDescription} onChange={(e) => setNewDesc(e.target.value)}
                className="glass-input" placeholder="Opsional" />
            </div>
            <div>
              <button type="submit" className="btn-primary w-full"><PlusIcon />Buat</button>
            </div>
          </form>
        </div>
      )}

      {/* ── daftar kelompok ── */}
      <div id="daftar-kelompok" className="glass-table-wrap scroll-mt-20">
        <table className="glass-table">
          <thead>
            <tr>
              <th>Kelompok</th>
              <th>Modul</th>
              <th>Cadangan</th>
              <th>Gudang</th>
              <th>Anggota</th>
              <th>Status</th>
              <th aria-label="Aksi"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-muted">Memuat…</td></tr>
            )}
            {!loading && teams.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-muted">Belum ada kelompok.</td></tr>
            )}
            {!loading && teams.map((team) => {
              const editing = editingId === team.id;
              const members = membersOf(team.id);
              const expanded = expandedTeamId === team.id;

              return (
                <Fragment key={team.id}>
                  <tr>
                    <td className="px-5 py-3">
                      {editing ? (
                        <input type="text" value={editDraft.name}
                          onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                          className="glass-input" />
                      ) : (
                        <div className="flex min-w-0 flex-col gap-[3px]">
                          <span className="font-semibold text-[14px]" style={{ color: "var(--text-primary)" }}>
                            {team.name}
                          </span>
                          <span className="max-w-[320px] truncate text-[12px]" style={{ color: "var(--text-dim)" }}>
                            {team.description || team.slug}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {editing ? (
                        <CustomSelect value={editDraft.module}
                          onChange={(v) => setEditDraft((d) => ({ ...d, module: v }))}
                          options={MODULE_OPTIONS} />
                      ) : (
                        <span className="text-[14px]" style={{ color: "var(--text-soft)" }}>
                          {MODULE_LABEL[team.module] || team.module}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {editing ? (
                        <CustomSelect
                          value={String(editDraft.default_approver_team_id ?? "")}
                          onChange={(v) => setEditDraft((d) => ({ ...d, default_approver_team_id: v }))}
                          options={approverOptions.filter((o) => o.value !== String(team.id))}
                          placeholder="— tidak ada —" />
                      ) : (
                        <span className="text-[14px]"
                          style={{ color: team.default_approver_team_name ? "var(--text-soft)" : "var(--text-faint)" }}>
                          {team.default_approver_team_name || "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-[14px]"
                        style={{ color: team.owned_location_names?.length ? "var(--text-soft)" : "var(--text-faint)" }}>
                        {team.owned_location_names?.length ? team.owned_location_names.join(", ") : "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <button type="button"
                        onClick={() => { setExpandedTeamId(expanded ? null : team.id); setAddUserId(""); }}
                        aria-expanded={expanded}
                        title={expanded ? "Tutup daftar anggota" : "Lihat anggota"}
                        className="inline-flex items-center gap-1.5 text-[14px] font-semibold"
                        style={{ color: "var(--accent)" }}>
                        <UsersIcon />
                        {members.length}
                        <span className="text-[10px] font-normal" style={{ color: "var(--text-faint)" }}>
                          {expanded ? "▴" : "▾"}
                        </span>
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      {editing ? (
                        <label className="flex items-center gap-2 text-[12px]" style={{ color: "var(--text-secondary)" }}>
                          <input type="checkbox" checked={!!editDraft.is_active}
                            onChange={(e) => setEditDraft((d) => ({ ...d, is_active: e.target.checked }))} />
                          Aktif
                        </label>
                      ) : (
                        <span role="img" aria-label={team.is_active ? "Aktif" : "Nonaktif"}
                          title={team.is_active ? "Aktif" : "Nonaktif"}
                          className="inline-block h-[9px] w-[9px] rounded-full"
                          style={team.is_active
                            ? { background: "var(--success)", boxShadow: "0 0 8px rgba(95,211,141,0.6)" }
                            : { background: "var(--text-faint)" }} />
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {!isSuperadmin ? (
                        <span className="text-[12px]" style={{ color: "var(--text-secondary)" }}>—</span>
                      ) : editing ? (
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={saveEdit} className="btn-primary btn-sm">Simpan</button>
                          <button onClick={cancelEdit} className="btn-ghost btn-sm">Batal</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1.5">
                          <button type="button" onClick={() => startEdit(team)}
                            aria-label={`Edit ${team.name}`} title="Edit" className="icon-btn">
                            <PencilIcon />
                          </button>
                          <button type="button" onClick={() => handleDeleteTeam(team)}
                            aria-label={`Hapus ${team.name}`} title="Hapus" className="icon-btn icon-btn-danger">
                            <TrashIcon />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>

                  {expanded && (
                    <tr>
                      <td colSpan={7} className="px-5 py-4" style={{ background: "var(--bg-surface-hover)" }}>
                        <div className="text-[12px] font-semibold mb-3" style={{ color: "var(--text-primary)" }}>
                          Anggota {team.name}
                        </div>

                        {members.length === 0 && (
                          <div className="text-[12px] mb-3" style={{ color: "var(--text-secondary)" }}>
                            Belum ada anggota.
                          </div>
                        )}

                        {members.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {members.map((m) => {
                              // Role badge, not scope — approval rights are derived
                              // purely from role now (case 3), so scope no longer
                              // says anything about what a member can do here.
                              const memberUser = usersById.get(m.user_id);
                              const role = memberUser?.role;
                              // The "hard entry, no flags" complaint (case 2) — show
                              // which OTHER kelompok this member already sits in,
                              // right next to them, instead of leaving a superadmin
                              // to cross-reference the Users page by hand.
                              const otherTeams = (memberUser?.teams || []).filter((t) => t.id !== team.id);
                              return (
                                <div key={m.id} className="flex flex-wrap items-center gap-3">
                                  <span className="text-[13px] font-medium min-w-[120px]" style={{ color: "var(--text-primary)" }}>
                                    {m.username}
                                  </span>
                                  {role && (
                                    <span className={`role-badge ${ROLE_CLASS[role] || "role-user"}`}>
                                      {ROLE_LABEL[role] || role}
                                    </span>
                                  )}
                                  {otherTeams.length > 0 && (
                                    <span className="inline-flex items-center gap-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                                      juga di:
                                      <TeamBadgeList teams={otherTeams} max={2} />
                                    </span>
                                  )}
                                  {isSuperadmin && (
                                    <button onClick={() => handleRemoveMember(m)} className="btn-danger btn-sm">
                                      Keluarkan
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {isSuperadmin && (
                          <div className="flex flex-wrap items-end gap-3 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
                            <div className="w-[260px]">
                              <label className="block text-[11px] font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                                Tambah user
                              </label>
                              <CustomSelect value={addUserId} onChange={setAddUserId}
                                options={usersNotIn(team)} placeholder="Pilih user..." searchable />
                            </div>
                            <button onClick={() => handleAddMember(team.id)} className="btn-primary btn-sm">
                              Tambah
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── kepemilikan gudang ── */}
      <div id="kepemilikan-gudang" className="glass-panel p-5 scroll-mt-20">
        <h2 className="text-[15px] font-semibold mb-1" style={{ color: "var(--text-primary)" }}>
          Kepemilikan Gudang
        </h2>
        <p className="text-[12px] mb-4" style={{ color: "var(--text-secondary)" }}>
          Pemilik melihat lalu-lintas gudangnya; &ldquo;yang meng-ACC&rdquo; (kalau kosong,
          pemilik) memutuskan pengajuan. Stok tetap terlihat semua kelompok.
        </p>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {loading && <div className="text-[12px] text-muted">Memuat…</div>}
          {!loading && locations.length === 0 && (
            <div className="text-[12px] text-muted">Belum ada gudang.</div>
          )}
          {!loading && locations.map((loc) => {
            // Neither set = requests here route nowhere and only a superadmin
            // can ever ACC them. A delegated approver alone is enough to route.
            const unrouted = !loc.owner_team_id && !loc.approver_team_id;
            return (
              <div key={loc.id} className="rounded-xl p-3"
                style={{
                  border: "1px solid",
                  borderColor: unrouted
                    ? "color-mix(in srgb, var(--danger) 35%, transparent)"
                    : "var(--border)",
                }}>
                <div className="text-[13px] font-semibold mb-3 flex items-center gap-2"
                  style={{ color: "var(--text-primary)" }}>
                  {loc.name}
                  {unrouted && <span className="team-tag team-tag-none">tanpa pemilik</span>}
                </div>

                <label className="block text-[11px] font-medium mb-1.5"
                  style={{ color: "var(--text-secondary)" }}>
                  Pemilik (yang memegang barang)
                </label>
                {isSuperadmin ? (
                  <CustomSelect
                    value={String(loc.owner_team_id ?? "")}
                    onChange={(v) => patchLocationTeam(loc.id, "owner_team_id", v, "Pemilik gudang diubah.")}
                    options={ownerOptions}
                    placeholder="— belum ada pemilik —"
                  />
                ) : (
                  <span className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    {loc.owner_team_name || "— belum ada pemilik —"}
                  </span>
                )}

                <label className="block text-[11px] font-medium mb-1.5 mt-3"
                  style={{ color: "var(--text-secondary)" }}>
                  Yang meng-ACC
                </label>
                {isSuperadmin ? (
                  <CustomSelect
                    value={String(loc.approver_team_id ?? "")}
                    onChange={(v) => patchLocationTeam(loc.id, "approver_team_id", v, "Kelompok yang meng-ACC diubah.")}
                    options={gudangApproverOptions}
                    placeholder="— sama dengan pemilik —"
                  />
                ) : (
                  <span className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    {loc.approver_team_name || "— sama dengan pemilik —"}
                  </span>
                )}

                {loc.routing_team_name && (
                  <div className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }}>
                    Pengajuan di sini masuk ke{" "}
                    <span className="team-tag">{loc.routing_team_name}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Roles;
